export const BREAKPOINTS = {
    xxs: '(max-width: 319.98px)',
    xs: '(min-width: 320px) and (max-width: 575.98px)',
    sm: '(min-width: 576px) and (max-width: 767.98px)',
    md: '(min-width: 768px) and (max-width: 1023.98px)',
    lg: '(min-width: 1024px) and (max-width: 1199.98px)',
    xl: '(min-width: 1200px) and (max-width: 1399.98px)',
    xxl: '(min-width: 1400px) and (max-width: 1699.98px)',
    xxxl: '(min-width: 1700px) and (max-width: 2399.98px)',
    xxxxl: '(min-width: 2400px)',
};

// Export breakpoint group constants
export const BREAKPOINT_GROUP_MOBILE = 'mobile';
export const BREAKPOINT_GROUP_TABLET = 'tablet';
export const BREAKPOINT_GROUP_DESKTOP = 'desktop';
export const BREAKPOINT_GROUP_LARGE_DESKTOP = 'largeDesktop';

/**
 * @type {object} Maps breakpoint group names to the array of breakpoints
 * that fall under them
 */
export const BREAKPOINT_GROUPS = {
    [BREAKPOINT_GROUP_MOBILE]: ['xxs', 'xs', 'sm'],
    [BREAKPOINT_GROUP_TABLET]: ['md'],
    [BREAKPOINT_GROUP_DESKTOP]: ['lg'],
    [BREAKPOINT_GROUP_LARGE_DESKTOP]: ['xl', 'xxl', 'xxxl', 'xxxxl'],
};

/**
 * Gets the breakpoint group the breakpoint falls under
 * @param {string} breakpoint 
 * @returns {null|string}
 */
export function getBreakpointGroup(breakpoint) {
    for (const [group, breakpoints] of Object.entries(BREAKPOINT_GROUPS)) {
        if (breakpoints.includes(breakpoint)) {
            return group;
        }
    }
    return null;
}

/**
 * Returns whether the breakpoint group is considered mobile
 * @param {string} breakpointGroup 
 * @returns {boolean}
 */
export function getIsMobile(breakpointGroup) {
    return breakpointGroup == BREAKPOINT_GROUP_MOBILE;
}

/**
 * Returns whether the breakpoint group is considered a small screen
 * @param {string} breakpointGroup 
 * @returns {boolean}
 */
export function getIsSmallScreen(breakpointGroup) {
    return [BREAKPOINT_GROUP_MOBILE, BREAKPOINT_GROUP_TABLET].includes(breakpointGroup);
}

/**
 * Returns whether the breakpoint group is considered a large screen
 * @param {string} breakpointGroup 
 * @returns {boolean}
 */
export function getIsLargeScreen(breakpointGroup) {
    return [BREAKPOINT_GROUP_DESKTOP, BREAKPOINT_GROUP_LARGE_DESKTOP].includes(breakpointGroup);
}

export class MediaQueryManager {
    constructor() {
        this.currentBreakpoint = null;
        this.currentBreakpointGroup = null;
        this.mediaQueries = [];
        this.listeners = new Set();
        this.isInitialized = false;
    }

    getCurrentBreakpoint() {
        for (const [name, query] of Object.entries(BREAKPOINTS)) {
            if (window.matchMedia(query).matches) {
                return name;
            }
        }
        return null;
    }


    getCurrentBreakpointGroup() {
        for (const [name, query] of Object.entries(BREAKPOINTS)) {
            if (window.matchMedia(query).matches) {
                return getBreakpointGroup(name);
            }
        }
        return null;
    }

    updateCurrentBreakpointData() {
        const newBreakpoint = this.getCurrentBreakpoint();
        if (newBreakpoint !== this.currentBreakpoint) {
            this.currentBreakpoint = newBreakpoint;
            this.currentBreakpointGroup = getBreakpointGroup(newBreakpoint);
            this.notifyListeners();
        }
    }

    addListener(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    notifyListeners() {
        this.listeners.forEach(callback => callback(this.currentBreakpoint, this.currentBreakpointGroup));
    }

    init() {
        if (this.isInitialized) return;

        this.updateCurrentBreakpointData();

        for (const query of Object.values(BREAKPOINTS)) {
            const mediaQuery = window.matchMedia(query);
            mediaQuery.addEventListener('change', () => this.updateCurrentBreakpointData());
            this.mediaQueries.push(mediaQuery);
        }

        this.isInitialized = true;
    }

    // Computed-like getters
    get isMobile() {
        return getIsMobile(this.currentBreakpointGroup)
    }

    get isSmallScreen() {
        return getIsSmallScreen(this.currentBreakpointGroup)
    }

    get isLargeScreen() {
        return getIsLargeScreen(this.currentBreakpointGroup)
    }
}

// Export singleton instance
export const mediaQueryManager = new MediaQueryManager();
